/**
 * @fileoverview Structured data (JSON-LD) components for SEO.
 */

import { useEffect, useId } from 'react';
import { customerConfig } from '@/lib/config/customer.config';
import { generateLocalBusinessSchema, generateCanonicalUrl } from './seo.config';
import type { BreadcrumbSchema, WebPageSchema, BreadcrumbItem } from './_types';

/**
 * Inject JSON-LD script into head
 */
function injectJsonLd(id: string, data: object): void {
  // Remove existing script with same ID
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Remove JSON-LD script from head
 */
function removeJsonLd(id: string): void {
  const script = document.getElementById(id);
  if (script) {
    script.remove();
  }
}

/**
 * LocalBusiness structured data component.
 * Auto-generates from customer config.
 */
export function LocalBusinessStructuredData() {
  const id = useId();
  const scriptId = `ld-local-business-${id}`;

  useEffect(() => {
    const schema = generateLocalBusinessSchema();
    injectJsonLd(scriptId, schema);

    return () => {
      removeJsonLd(scriptId);
    };
  }, [scriptId]);

  return null;
}

interface BreadcrumbsStructuredDataProps {
  /** Array of breadcrumb items */
  items: Array<{
    name: string;
    url?: string;
  }>;
}

/**
 * Breadcrumbs structured data component.
 */
export function BreadcrumbsStructuredData({ items }: BreadcrumbsStructuredDataProps) {
  const id = useId();
  const scriptId = `ld-breadcrumbs-${id}`;

  useEffect(() => {
    const breadcrumbItems: BreadcrumbItem[] = items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? generateCanonicalUrl(item.url) : undefined,
    }));

    const schema: BreadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems,
    };

    injectJsonLd(scriptId, schema);

    return () => {
      removeJsonLd(scriptId);
    };
  }, [items, scriptId]);

  return null;
}

interface WebPageStructuredDataProps {
  /** Page name */
  name: string;
  /** Page description */
  description?: string;
  /** Page URL path */
  url?: string;
}

/**
 * WebPage structured data component.
 */
export function WebPageStructuredData({
  name,
  description,
  url,
}: WebPageStructuredDataProps) {
  const id = useId();
  const scriptId = `ld-webpage-${id}`;

  useEffect(() => {
    const schema: WebPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name,
      description,
      url: url ? generateCanonicalUrl(url) : generateCanonicalUrl(window.location.pathname),
      inLanguage: 'en-AU',
      isPartOf: {
        '@type': 'WebSite',
        name: customerConfig.businessName,
        url: customerConfig.websiteUrl,
      },
    };

    injectJsonLd(scriptId, schema);

    return () => {
      removeJsonLd(scriptId);
    };
  }, [name, description, url, scriptId]);

  return null;
}

interface ReservationActionStructuredDataProps {
  /** Target URL for the reservation action */
  targetUrl?: string;
}

/**
 * Reservation action structured data.
 * Helps search engines understand booking capability.
 */
export function ReservationActionStructuredData({
  targetUrl = '/',
}: ReservationActionStructuredDataProps) {
  const id = useId();
  const scriptId = `ld-reservation-${id}`;

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Restaurant',
      name: customerConfig.businessName,
      url: customerConfig.websiteUrl,
      potentialAction: {
        '@type': 'ReserveAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: generateCanonicalUrl(targetUrl),
          actionPlatform: [
            'http://schema.org/DesktopWebPlatform',
            'http://schema.org/MobileWebPlatform',
          ],
        },
        result: {
          '@type': 'Reservation',
          name: 'Table Reservation',
        },
      },
    };

    injectJsonLd(scriptId, schema);

    return () => {
      removeJsonLd(scriptId);
    };
  }, [targetUrl, scriptId]);

  return null;
}

interface CustomStructuredDataProps {
  /** JSON-LD data object */
  data: Record<string, unknown>;
}

/**
 * Custom structured data component for arbitrary JSON-LD.
 */
export function CustomStructuredData({ data }: CustomStructuredDataProps) {
  const id = useId();
  const scriptId = `ld-custom-${id}`;

  useEffect(() => {
    injectJsonLd(scriptId, data);

    return () => {
      removeJsonLd(scriptId);
    };
  }, [data, scriptId]);

  return null;
}
